/**
 * 精确枚举 vs 蒙特卡洛的分流。见 docs/ARCHITECTURE.md §10.3。
 *
 * **不写死阶段**：同样是翻牌 N=1，桌面浏览器 1.07M 次评估只要 30ms，
 * 老手机要 500ms。写死「翻牌一律 MC」会白白放弃桌面端的精确值，
 * 写死「翻牌一律精确」会在手机上卡掉半秒。所以按实测 evalRate 估时间。
 *
 * 红线 #30：这里的所有决策只依赖 (街, 对手数, 模型, 设备速率)，
 * **绝不依赖底牌强弱**。迭代数与耗时不得携带任何牌力信息，
 * 否则「AI 想了多久」本身就是一条边信道。
 */
import { InvariantError } from '../poker/types.js'
import { NUM_CARDS } from '../poker/cards.js'
import {
  DEFAULT_TARGET_ITERATIONS,
  type EquityRequest,
  type EquityStage,
  MAX_OPPONENTS,
} from './types.js'

/** 精确枚举的时间预算（§10.3）。超过就走蒙特卡洛。 */
export const EXACT_BUDGET_MS = 250

/**
 * 硬上限：即使调用方喂进来一个离谱的 evalRate，也绝不启动超过这个量级的枚举。
 * 枚举一旦开始就跑到底（不许提前终止），一个被高估 100 倍的速率会把
 * 「250ms 预算」变成 25 秒的卡死。
 */
export const EXACT_HARD_EVAL_CAP = 40_000_000

/** Worker 标定前的保守默认值（§10.3 提到的手机基准 2M evals/s） */
export const DEFAULT_EVAL_RATE = 2_000_000

/** 渐进式返回的累计迭代节点（§10.4 的 S0 / S1 / S2） */
export const MC_STAGE_ITERATIONS: readonly number[] = Object.freeze([2_000, 10_000, 40_000])

export type EquityMethodPlan = 'trivial' | 'table' | 'exact' | 'montecarlo'

export interface EquityPlan {
  readonly method: EquityMethodPlan
  readonly stage: EquityStage
  /** 未知牌数 U = 52 − 2 − |board| − |publicDead| */
  readonly unknownCards: number
  /** 精确枚举要评估的发牌组合数（对手牌 × runout） */
  readonly exactDeals: number
  /** 精确枚举总评估次数 ≈ hero 每个 runout 一次 + 每个组合一次 */
  readonly exactEvals: number
  readonly estMs: number
  /** 蒙特卡洛的累计迭代节点，最后一个即目标迭代数 */
  readonly stages: readonly number[]
}

export function stageOfBoardLength(len: number): EquityStage {
  switch (len) {
    case 0:
      return 'preflop'
    case 3:
      return 'flop'
    case 4:
      return 'turn'
    case 5:
      return 'river'
    default:
      throw new InvariantError(`board 长度只能是 0/3/4/5，收到 ${len}`)
  }
}

function choose(n: number, k: number): number {
  if (k < 0 || k > n) return 0
  let r = 1
  for (let i = 0; i < k; i++) r = (r * (n - i)) / (i + 1)
  return r
}

/**
 * 精确组合数 = C(U,2) × C(U−2,2) × … (N 次) × C(U−2N, R)。
 *
 * 与「外层 runout、内层对手手牌」的实现顺序无关：两种顺序都等于
 * U! / (2!^N · R! · (U−2N−R)!)，差别只在 hero 要评估几次。
 */
export function exactDealCount(unknown: number, opponents: number, runout: number): number {
  let total = 1
  let left = unknown
  for (let i = 0; i < opponents; i++) {
    total *= choose(left, 2)
    left -= 2
  }
  return total * choose(left, runout)
}

/**
 * hero 的评估次数 = runout 数（外层循环每个 runout 只算一次自己的 7 张）。
 * 河牌时是 1 次，转牌 U 次，翻牌 C(U,2) 次。
 */
export function heroEvalCount(unknown: number, runout: number): number {
  return choose(unknown, runout)
}

export function planEquity(req: EquityRequest, evalRate: number = DEFAULT_EVAL_RATE): EquityPlan {
  const stage = stageOfBoardLength(req.board.length)
  const deadCount = req.publicDead?.length ?? 0
  const unknown = NUM_CARDS - 2 - req.board.length - deadCount
  const runout = 5 - req.board.length
  const target = Math.max(1, req.targetIterations ?? DEFAULT_TARGET_ITERATIONS)
  const stages = MC_STAGE_ITERATIONS.filter((s) => s < target).concat(target)

  if (req.opponents <= 0) {
    return {
      method: 'trivial',
      stage,
      unknownCards: unknown,
      exactDeals: 0,
      exactEvals: 0,
      estMs: 0,
      stages: [],
    }
  }
  if (req.opponents > MAX_OPPONENTS || !Number.isInteger(req.opponents)) {
    throw new InvariantError(`opponents 必须是 0..${MAX_OPPONENTS} 的整数，收到 ${req.opponents}`)
  }

  const exactDeals = exactDealCount(unknown, req.opponents, runout)
  const exactEvals = exactDeals + heroEvalCount(unknown, runout)
  const rate = evalRate > 0 ? evalRate : DEFAULT_EVAL_RATE
  const estMs = (exactEvals / rate) * 1000

  // 翻前只要是「对随机手牌」且没有公开亮出的死牌，就走预计算表：0 次迭代。
  // 有 publicDead（例如有人亮了牌）时表不再适用——那张牌改变了对手的牌池。
  if (stage === 'preflop' && req.model.kind === 'random' && deadCount === 0) {
    return {
      method: 'table',
      stage,
      unknownCards: unknown,
      exactDeals,
      exactEvals,
      estMs: 0,
      stages: [],
    }
  }

  const allowExact = req.allowExact !== false
  const feasible = exactEvals <= EXACT_HARD_EVAL_CAP && estMs <= EXACT_BUDGET_MS
  if (allowExact && feasible) {
    return {
      method: 'exact',
      stage,
      unknownCards: unknown,
      exactDeals,
      exactEvals,
      estMs,
      stages: [],
    }
  }

  return {
    method: 'montecarlo',
    stage,
    unknownCards: unknown,
    exactDeals,
    exactEvals,
    estMs,
    stages,
  }
}
